import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { driveAPI, userAPI, impostazioniAPI, adminAPI, modalitaPagamentoAPI, specialistiAPI } from '../lib/api';
import DriveBrowser from '../components/DriveBrowser';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  CloudOff,
  Loader2,
  Check,
  X,
  Key,
  Eye,
  EyeOff,
  Palette,
  Save,
  FileText,
  FolderOpen,
  User,
  Download,
  GitBranch,
  RefreshCw,
  HardDrive,
  CreditCard,
  Plus,
  Trash2,
  Type,
  Clock,
  Users,
  Package,
  Pencil,
} from 'lucide-react';

const GoogleDriveLogo = () => (
  <svg viewBox="0 0 87.3 78" className="w-6 h-6">
    <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066da"/>
    <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.2c-.8 1.4-1.2 2.95-1.2 4.5h27.5l16.15-31.7z" fill="#00ac47"/>
    <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.85l6.1 11.8 7.6 12z" fill="#ea4335"/>
    <path d="M43.65 25l13.75-23.8C56.05.4 54.5 0 52.95 0H34.35c-1.55 0-3.1.4-4.5 1.2L43.65 25z" fill="#00832d"/>
    <path d="M59.85 53H86.6l-13.2-22.9-1.95-3.4c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28z" fill="#2684fc"/>
    <path d="M27.5 53L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.55 0 3.1-.4 4.5-1.2L59.85 53H27.5z" fill="#ffba00"/>
  </svg>
);

const AVAILABLE_FONTS = [
  'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana',
  'Trebuchet MS', 'Courier New', 'Palatino', 'Garamond', 'Bookman',
  'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Raleway',
];

const Impostazioni = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [driveStatus, setDriveStatus] = useState({ connected: false, loading: true });
  const [connecting, setConnecting] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  
  // Password dialog state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Impostazioni preventivo
  const [impostazioniPreventivo, setImpostazioniPreventivo] = useState({
    font_testo: 'Arial',
    colore_testo: '#333333',
    font_titoli: 'Arial',
    colore_titoli: '#1a1a1a',
    colore_accent: '#f97316',
    tempi_realizzazione: '',
    specialisti: '',
    materiale_cliente: '',
  });
  const [savingImpostazioni, setSavingImpostazioni] = useState(false);

  // Drive settings
  const [driveSettings, setDriveSettings] = useState({
    template_id: null,
    template_name: null,
    folder_id: null,
    folder_name: null,
  });
  const [savingDriveSettings, setSavingDriveSettings] = useState(false);
  
  // Template/Folder selection dialogs
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);

  // Upgrade state
  const [upgrading, setUpgrading] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeResult, setUpgradeResult] = useState(null);

  // Backup state
  const [backingUp, setBackingUp] = useState(false);
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [backupResult, setBackupResult] = useState(null);

  // Modalità Pagamento state
  const [modalitaPagamento, setModalitaPagamento] = useState([]);
  const [newModalita, setNewModalita] = useState({ nome: '', descrizione: '' });
  const [addingModalita, setAddingModalita] = useState(false);
  const [modalitaDialogOpen, setModalitaDialogOpen] = useState(false);
  const [editingModalita, setEditingModalita] = useState(null);

  // Specialisti state
  const [specialisti, setSpecialisti] = useState([]);
  const [newSpecialista, setNewSpecialista] = useState({ specialista: '', descrizione: '' });
  const [addingSpecialista, setAddingSpecialista] = useState(false);
  const [specialistiDialogOpen, setSpecialistiDialogOpen] = useState(false);
  const [editingSpecialista, setEditingSpecialista] = useState(null);

  // Formattazione Documento state
  const [formattazione, setFormattazione] = useState({
    h1: { size: 16, color: '#f97316', bold: true, indent: 0 },
    h2: { size: 14, color: '#1e40af', bold: true, indent: 0 },
    h3: { size: 12, color: '#333333', bold: true, indent: 0 },
    h4: { size: 11, color: '#333333', bold: true, indent: 0 },
    p: { size: 11, color: '#333333', bold: false, indent: 0 },
    li: { size: 11, color: '#333333', bold: false, indent: 18 },
  });
  const [savingFormattazione, setSavingFormattazione] = useState(false);

  useEffect(() => {
    checkDriveStatus();
    fetchImpostazioniPreventivo();
    fetchVersionInfo();
    fetchModalitaPagamento();
    fetchFormattazione();
    fetchSpecialisti();
    
    const driveConnected = searchParams.get('drive_connected');
    const driveError = searchParams.get('drive_error');
    
    if (driveConnected === 'true') {
      toast.success('Google Drive connesso con successo!');
      checkDriveStatus();
    } else if (driveError) {
      toast.error(`Errore connessione Drive: ${driveError}`);
    }
  }, [searchParams]);

  const fetchSpecialisti = async () => {
    try {
      const response = await specialistiAPI.list();
      setSpecialisti(response.data);
    } catch (error) {
      console.error('Error fetching specialisti:', error);
    }
  };

  const handleAddSpecialista = async () => {
    if (!newSpecialista.specialista.trim()) {
      toast.error('Inserisci il nome dello specialista');
      return;
    }
    setAddingSpecialista(true);
    try {
      if (editingSpecialista) {
        await specialistiAPI.update(editingSpecialista.id, newSpecialista);
        toast.success('Specialista aggiornato!');
      } else {
        await specialistiAPI.create(newSpecialista);
        toast.success('Specialista aggiunto!');
      }
      setNewSpecialista({ specialista: '', descrizione: '' });
      setEditingSpecialista(null);
      setSpecialistiDialogOpen(false);
      fetchSpecialisti();
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setAddingSpecialista(false);
    }
  };

  const handleEditSpecialista = (specialista) => {
    setEditingSpecialista(specialista);
    setNewSpecialista({ 
      specialista: specialista.specialista || specialista.nome || '', 
      descrizione: specialista.descrizione || specialista.ruolo || '' 
    });
    setSpecialistiDialogOpen(true);
  };

  const handleDeleteSpecialista = async (id) => {
    try {
      await specialistiAPI.delete(id);
      toast.success('Specialista eliminato');
      fetchSpecialisti();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const fetchFormattazione = async () => {
    try {
      const response = await impostazioniAPI.getFormattazione();
      setFormattazione(response.data);
    } catch (error) {
      console.error('Error fetching formattazione:', error);
    }
  };

  const handleSaveFormattazione = async () => {
    setSavingFormattazione(true);
    try {
      await impostazioniAPI.updateFormattazione(formattazione);
      toast.success('Formattazione salvata!');
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSavingFormattazione(false);
    }
  };

  const updateTagStyle = (tag, field, value) => {
    setFormattazione({
      ...formattazione,
      [tag]: {
        ...formattazione[tag],
        [field]: value
      }
    });
  };

  const fetchModalitaPagamento = async () => {
    try {
      const response = await modalitaPagamentoAPI.list();
      setModalitaPagamento(response.data);
    } catch (error) {
      console.error('Error fetching modalità pagamento:', error);
    }
  };

  const handleAddModalita = async () => {
    if (!newModalita.nome.trim()) {
      toast.error('Inserisci un nome per la modalità');
      return;
    }
    setAddingModalita(true);
    try {
      if (editingModalita) {
        await modalitaPagamentoAPI.update(editingModalita.id, newModalita);
        toast.success('Modalità aggiornata!');
      } else {
        await modalitaPagamentoAPI.create(newModalita);
        toast.success('Modalità aggiunta!');
      }
      setNewModalita({ nome: '', descrizione: '' });
      setEditingModalita(null);
      setModalitaDialogOpen(false);
      fetchModalitaPagamento();
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setAddingModalita(false);
    }
  };

  const handleEditModalita = (modalita) => {
    setEditingModalita(modalita);
    setNewModalita({ nome: modalita.nome, descrizione: modalita.descrizione || '' });
    setModalitaDialogOpen(true);
  };

  const handleDeleteModalita = async (id) => {
    try {
      await modalitaPagamentoAPI.delete(id);
      toast.success('Modalità eliminata');
      fetchModalitaPagamento();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const fetchVersionInfo = async () => {
    try {
      const response = await adminAPI.getVersion();
      setVersionInfo(response.data);
    } catch (error) {
      console.error('Error fetching version:', error);
    }
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    setUpgradeResult(null);
    try {
      const response = await adminAPI.upgrade();
      setUpgradeResult(response.data);
      toast.success('Upgrade completato!');
      fetchVersionInfo();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'upgrade';
      toast.error(message);
      setUpgradeResult({ success: false, message });
    } finally {
      setUpgrading(false);
    }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    setBackupResult(null);
    try {
      const response = await adminAPI.backup();
      setBackupResult(response.data);
      toast.success('Backup completato!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante il backup';
      toast.error(message);
      setBackupResult({ success: false, message });
    } finally {
      setBackingUp(false);
    }
  };

  const checkDriveStatus = async () => {
    try {
      const response = await driveAPI.status();
      setDriveStatus({ connected: response.data.connected, loading: false });
      
      if (response.data.connected) {
        fetchDriveSettings();
      }
    } catch (error) {
      setDriveStatus({ connected: false, loading: false });
    }
  };

  const fetchDriveSettings = async () => {
    try {
      const response = await driveAPI.getSettings();
      setDriveSettings(response.data);
    } catch (error) {
      console.error('Errore caricamento impostazioni Drive:', error);
    }
  };

  const fetchImpostazioniPreventivo = async () => {
    try {
      const response = await impostazioniAPI.getPreventivo();
      setImpostazioniPreventivo(response.data);
    } catch (error) {
      console.error('Errore caricamento impostazioni:', error);
    }
  };

  const handleConnectDrive = async () => {
    setConnecting(true);
    try {
      const response = await driveAPI.connect();
      window.location.href = response.data.authorization_url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante la connessione';
      toast.error(message);
      setConnecting(false);
    }
  };

  const handleDisconnectDrive = async () => {
    setDisconnecting(true);
    try {
      await driveAPI.disconnect();
      setDriveStatus({ connected: false, loading: false });
      setDriveSettings({ template_id: null, template_name: null, folder_id: null, folder_name: null });
      toast.success('Google Drive disconnesso');
      setDisconnectDialogOpen(false);
    } catch (error) {
      toast.error('Errore durante la disconnessione');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Le nuove password non corrispondono');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('La nuova password deve essere di almeno 6 caratteri');
      return;
    }
    
    setChangingPassword(true);
    try {
      await userAPI.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });
      toast.success('Password aggiornata con successo!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordDialogOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante il cambio password';
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveImpostazioni = async () => {
    setSavingImpostazioni(true);
    try {
      await impostazioniAPI.updatePreventivo(impostazioniPreventivo);
      toast.success('Impostazioni salvate!');
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSavingImpostazioni(false);
    }
  };

  const handleSaveDriveSettings = async () => {
    setSavingDriveSettings(true);
    try {
      await driveAPI.updateSettings(driveSettings);
      toast.success('Impostazioni Drive salvate!');
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSavingDriveSettings(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setDriveSettings({
      ...driveSettings,
      template_id: template.id,
      template_name: template.name,
    });
  };

  const handleSelectFolder = (folder) => {
    setDriveSettings({
      ...driveSettings,
      folder_id: folder.id,
      folder_name: folder.name,
    });
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="impostazioni-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Impostazioni</h1>
          <p className="text-slate-500 mt-1">Gestisci le configurazioni del tuo account</p>
        </div>

        {/* User Profile with Change Password Button */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg font-heading">{user?.nome} {user?.cognome}</CardTitle>
                <CardDescription>{user?.email}</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPasswordDialogOpen(true)}
                data-testid="change-password-btn"
              >
                <Key className="w-4 h-4 mr-2" />
                Cambia Password
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-sm text-slate-500">
              Account creato il {user?.created_at ? new Date(user.created_at).toLocaleDateString('it-IT') : '-'}
            </div>
          </CardContent>
        </Card>

        {/* Google Drive Integration */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <GoogleDriveLogo />
              <div className="flex-1">
                <CardTitle className="text-lg font-heading">Google Drive</CardTitle>
                <CardDescription>
                  Connetti il tuo Google Drive per generare documenti dai template
                </CardDescription>
              </div>
              {driveStatus.connected && (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  <Check className="w-3 h-3 mr-1" />
                  Connesso
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {driveStatus.loading ? (
              <div className="flex items-center gap-3 py-4">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                <span className="text-slate-500">Verifica connessione...</span>
              </div>
            ) : driveStatus.connected ? (
              <>
                {/* Template Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4" />
                      Modello Predefinito
                    </Label>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1 justify-start h-10 font-normal"
                        onClick={() => setTemplateDialogOpen(true)}
                      >
                        {driveSettings.template_name || 'Seleziona modello...'}
                      </Button>
                      {driveSettings.template_id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDriveSettings({...driveSettings, template_id: null, template_name: null})}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm">
                      <FolderOpen className="w-4 h-4" />
                      Cartella Destinazione
                    </Label>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1 justify-start h-10 font-normal"
                        onClick={() => setFolderDialogOpen(true)}
                      >
                        {driveSettings.folder_name || 'Seleziona cartella...'}
                      </Button>
                      {driveSettings.folder_id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDriveSettings({...driveSettings, folder_id: null, folder_name: null})}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleSaveDriveSettings}
                    disabled={savingDriveSettings}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    {savingDriveSettings ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Salva Impostazioni Drive
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setDisconnectDialogOpen(true)}
                    data-testid="disconnect-drive-btn"
                  >
                    <CloudOff className="w-4 h-4 mr-2" />
                    Disconnetti
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <X className="w-5 h-5 text-slate-400" />
                  <div className="flex-1">
                    <p className="font-medium text-slate-700">Non connesso</p>
                    <p className="text-sm text-slate-500">
                      Connetti Google Drive per generare documenti
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleConnectDrive}
                  disabled={connecting}
                  className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                  data-testid="connect-drive-btn"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connessione...
                    </>
                  ) : (
                    <>
                      <GoogleDriveLogo />
                      <span className="ml-2">Connetti Google Drive</span>
                    </>
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Impostazioni Preventivo - Compatto */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Palette className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-heading">Stile Preventivo</CardTitle>
                <CardDescription>Font e colori per i documenti generati</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Preview compatto */}
            <div className="p-4 border border-slate-200 rounded-lg bg-white">
              <h3 
                className="text-lg font-bold mb-1"
                style={{ fontFamily: impostazioniPreventivo.font_titoli, color: impostazioniPreventivo.colore_titoli }}
              >
                Anteprima Titolo
              </h3>
              <p 
                className="text-sm mb-2"
                style={{ fontFamily: impostazioniPreventivo.font_testo, color: impostazioniPreventivo.colore_testo }}
              >
                Testo di esempio con il font e colore selezionati.
              </p>
              <span 
                className="inline-block px-2 py-0.5 rounded text-white text-xs"
                style={{ backgroundColor: impostazioniPreventivo.colore_accent }}
              >
                Accent
              </span>
            </div>

            {/* Grid compatto */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Font Titoli</Label>
                <Select 
                  value={impostazioniPreventivo.font_titoli} 
                  onValueChange={(v) => setImpostazioniPreventivo({...impostazioniPreventivo, font_titoli: v})}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_FONTS.map(font => (
                      <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                        {font}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Colore Titoli</Label>
                <div className="flex gap-1">
                  <Input
                    type="color"
                    value={impostazioniPreventivo.colore_titoli}
                    onChange={(e) => setImpostazioniPreventivo({...impostazioniPreventivo, colore_titoli: e.target.value})}
                    className="w-10 h-9 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={impostazioniPreventivo.colore_titoli}
                    onChange={(e) => setImpostazioniPreventivo({...impostazioniPreventivo, colore_titoli: e.target.value})}
                    className="flex-1 h-9 font-mono text-xs"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Colore Accent</Label>
                <div className="flex gap-1">
                  <Input
                    type="color"
                    value={impostazioniPreventivo.colore_accent}
                    onChange={(e) => setImpostazioniPreventivo({...impostazioniPreventivo, colore_accent: e.target.value})}
                    className="w-10 h-9 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={impostazioniPreventivo.colore_accent}
                    onChange={(e) => setImpostazioniPreventivo({...impostazioniPreventivo, colore_accent: e.target.value})}
                    className="flex-1 h-9 font-mono text-xs"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Font Testo</Label>
                <Select 
                  value={impostazioniPreventivo.font_testo} 
                  onValueChange={(v) => setImpostazioniPreventivo({...impostazioniPreventivo, font_testo: v})}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_FONTS.map(font => (
                      <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                        {font}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1 col-span-2 md:col-span-2">
                <Label className="text-xs">Colore Testo</Label>
                <div className="flex gap-1">
                  <Input
                    type="color"
                    value={impostazioniPreventivo.colore_testo}
                    onChange={(e) => setImpostazioniPreventivo({...impostazioniPreventivo, colore_testo: e.target.value})}
                    className="w-10 h-9 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={impostazioniPreventivo.colore_testo}
                    onChange={(e) => setImpostazioniPreventivo({...impostazioniPreventivo, colore_testo: e.target.value})}
                    className="flex-1 h-9 font-mono text-xs"
                  />
                </div>
              </div>
            </div>

            <Button
              onClick={handleSaveImpostazioni}
              disabled={savingImpostazioni}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {savingImpostazioni ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salva Stile
            </Button>
          </CardContent>
        </Card>

        {/* Contenuti Documento */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-heading">Contenuti Documento</CardTitle>
                <CardDescription>Testi predefiniti per il preventivo</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500 mb-4">
              Questi contenuti appariranno nel documento prima della tabella dei prezzi.
              Inserisci nel template i segnaposto: <code className="bg-slate-100 px-1 rounded">{"{{TEMPI_REALIZZAZIONE}}"}</code>, 
              <code className="bg-slate-100 px-1 rounded ml-1">{"{{SPECIALISTI}}"}</code>, 
              <code className="bg-slate-100 px-1 rounded ml-1">{"{{MATERIALE_CLIENTE}}"}</code>
            </p>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-teal-600" />
                  <Label>Tempi di Realizzazione</Label>
                </div>
                <Textarea
                  value={impostazioniPreventivo.tempi_realizzazione || ''}
                  onChange={(e) => setImpostazioniPreventivo({
                    ...impostazioniPreventivo,
                    tempi_realizzazione: e.target.value
                  })}
                  placeholder="es. La realizzazione del progetto richiederà circa 30 giorni lavorativi dalla data di accettazione del preventivo."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-teal-600" />
                  <Label>Materiale Richiesto al Cliente</Label>
                </div>
                <Textarea
                  value={impostazioniPreventivo.materiale_cliente || ''}
                  onChange={(e) => setImpostazioniPreventivo({
                    ...impostazioniPreventivo,
                    materiale_cliente: e.target.value
                  })}
                  placeholder="es. Per procedere con il progetto, il cliente dovrà fornire: logo in formato vettoriale, contenuti testuali, immagini ad alta risoluzione."
                  rows={3}
                />
              </div>
            </div>

            <Button
              onClick={handleSaveImpostazioni}
              disabled={savingImpostazioni}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {savingImpostazioni ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salva Contenuti
            </Button>
          </CardContent>
        </Card>

        {/* Formattazione Documento */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Type className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-heading">Formattazione Documento</CardTitle>
                <CardDescription>Configura dimensione e colore dei tag HTML per il documento</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500 mb-4">
              Usa questi tag nell'editor dei servizi: <code className="bg-slate-100 px-1 rounded">H1</code> per titoli principali, 
              <code className="bg-slate-100 px-1 rounded ml-1">H2</code> per sottotitoli, 
              <code className="bg-slate-100 px-1 rounded ml-1">H3/H4</code> per sotto-sezioni.
            </p>
            
            {/* Tag Grid */}
            <div className="space-y-3">
              {[
                { tag: 'h1', label: 'Titolo H1', desc: 'Titolo principale servizio' },
                { tag: 'h2', label: 'Titolo H2', desc: 'Sottotitoli/Feature' },
                { tag: 'h3', label: 'Titolo H3', desc: 'Sotto-sezioni' },
                { tag: 'h4', label: 'Titolo H4', desc: 'Dettagli' },
                { tag: 'p', label: 'Paragrafo', desc: 'Testo normale' },
                { tag: 'li', label: 'Lista (•)', desc: 'Elementi elenco' },
              ].map(({ tag, label, desc }) => (
                <div key={tag} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="w-24">
                    <p className="font-medium text-slate-900">{label}</p>
                    <p className="text-xs text-slate-500">{desc}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs w-12">Dim.</Label>
                      <Input
                        type="number"
                        min="8"
                        max="36"
                        value={formattazione[tag]?.size || 11}
                        onChange={(e) => updateTagStyle(tag, 'size', parseInt(e.target.value) || 11)}
                        className="w-16 h-8 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs w-12">Colore</Label>
                      <Input
                        type="color"
                        value={formattazione[tag]?.color || '#333333'}
                        onChange={(e) => updateTagStyle(tag, 'color', e.target.value)}
                        className="w-10 h-8 p-1 cursor-pointer"
                      />
                      <Input
                        type="text"
                        value={formattazione[tag]?.color || '#333333'}
                        onChange={(e) => updateTagStyle(tag, 'color', e.target.value)}
                        className="w-20 h-8 text-xs font-mono"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={formattazione[tag]?.bold || false}
                        onCheckedChange={(checked) => updateTagStyle(tag, 'bold', checked)}
                      />
                      <Label className="text-xs">Grassetto</Label>
                    </div>
                    {tag === 'li' && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs w-12">Indent</Label>
                        <Input
                          type="number"
                          min="0"
                          max="72"
                          value={formattazione[tag]?.indent || 0}
                          onChange={(e) => updateTagStyle(tag, 'indent', parseInt(e.target.value) || 0)}
                          className="w-16 h-8 text-sm"
                        />
                      </div>
                    )}
                  </div>
                  {/* Preview */}
                  <div 
                    className="w-32 text-right"
                    style={{
                      fontSize: `${Math.min(formattazione[tag]?.size || 11, 16)}px`,
                      color: formattazione[tag]?.color || '#333333',
                      fontWeight: formattazione[tag]?.bold ? 'bold' : 'normal',
                    }}
                  >
                    Anteprima
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={handleSaveFormattazione}
              disabled={savingFormattazione}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {savingFormattazione ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salva Formattazione
            </Button>
          </CardContent>
        </Card>

        {/* Modalità di Pagamento */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-heading">Modalità di Pagamento</CardTitle>
                  <CardDescription>Definisci le opzioni di pagamento per i preventivi</CardDescription>
                </div>
              </div>
              <Button
                onClick={() => {
                  setEditingModalita(null);
                  setNewModalita({ nome: '', descrizione: '' });
                  setModalitaDialogOpen(true);
                }}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {modalitaPagamento.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <CreditCard className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>Nessuna modalità di pagamento configurata</p>
                <p className="text-sm">Aggiungi le modalità che verranno mostrate nei preventivi</p>
              </div>
            ) : (
              <div className="space-y-2">
                {modalitaPagamento.map((modalita) => (
                  <div
                    key={modalita.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{modalita.nome}</p>
                      {modalita.descrizione && (
                        <p className="text-sm text-slate-500">{modalita.descrizione}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditModalita(modalita)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteModalita(modalita.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Specialisti */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-heading">Specialisti</CardTitle>
                  <CardDescription>Gestisci il team da associare ai preventivi</CardDescription>
                </div>
              </div>
              <Button
                onClick={() => {
                  setEditingSpecialista(null);
                  setNewSpecialista({ specialista: '', descrizione: '' });
                  setSpecialistiDialogOpen(true);
                }}
                size="sm"
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="add-specialista-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {specialisti.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>Nessuno specialista configurato</p>
                <p className="text-sm">Aggiungi i membri del team che lavoreranno ai progetti</p>
              </div>
            ) : (
              <div className="space-y-2">
                {specialisti.map((specialista) => (
                  <div
                    key={specialista.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                    data-testid={`specialista-item-${specialista.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{specialista.specialista || specialista.nome}</p>
                      {(specialista.descrizione || specialista.ruolo) && (
                        <p className="text-sm text-slate-500">{specialista.descrizione || specialista.ruolo}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditSpecialista(specialista)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSpecialista(specialista.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-specialista-${specialista.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog Aggiungi/Modifica Modalità */}
        <Dialog open={modalitaDialogOpen} onOpenChange={(open) => {
          setModalitaDialogOpen(open);
          if (!open) {
            setEditingModalita(null);
            setNewModalita({ nome: '', descrizione: '' });
          }
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">{editingModalita ? 'Modifica' : 'Nuova'} Modalità di Pagamento</DialogTitle>
              <DialogDescription>
                Inserisci i dettagli della modalità di pagamento
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="modalita-nome">Nome *</Label>
                <Input
                  id="modalita-nome"
                  value={newModalita.nome}
                  onChange={(e) => setNewModalita({ ...newModalita, nome: e.target.value })}
                  placeholder="es. Bonifico Bancario"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="modalita-descrizione">Descrizione</Label>
                <Textarea
                  id="modalita-descrizione"
                  value={newModalita.descrizione}
                  onChange={(e) => setNewModalita({ ...newModalita, descrizione: e.target.value })}
                  placeholder="es. IBAN: IT00X0000000000000000000000"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalitaDialogOpen(false)}>
                Annulla
              </Button>
              <Button
                onClick={handleAddModalita}
                disabled={addingModalita}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {addingModalita ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : editingModalita ? (
                  <Save className="w-4 h-4 mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                {editingModalita ? 'Salva' : 'Aggiungi'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Aggiungi/Modifica Specialista */}
        <Dialog open={specialistiDialogOpen} onOpenChange={(open) => {
          setSpecialistiDialogOpen(open);
          if (!open) {
            setEditingSpecialista(null);
            setNewSpecialista({ specialista: '', descrizione: '' });
          }
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">{editingSpecialista ? 'Modifica' : 'Nuovo'} Specialista</DialogTitle>
              <DialogDescription>
                Inserisci i dettagli dello specialista
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="specialista-nome">Specialista *</Label>
                <Input
                  id="specialista-nome"
                  value={newSpecialista.specialista}
                  onChange={(e) => setNewSpecialista({ ...newSpecialista, specialista: e.target.value })}
                  placeholder="es. Mario Rossi - Project Manager"
                  data-testid="specialista-nome-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="specialista-descrizione">Descrizione</Label>
                <Textarea
                  id="specialista-descrizione"
                  value={newSpecialista.descrizione}
                  onChange={(e) => setNewSpecialista({ ...newSpecialista, descrizione: e.target.value })}
                  placeholder="es. Responsabile della gestione del progetto e coordinamento del team"
                  data-testid="specialista-ruolo-input"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSpecialistiDialogOpen(false)}>
                Annulla
              </Button>
              <Button
                onClick={handleAddSpecialista}
                disabled={addingSpecialista}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="specialista-submit-btn"
              >
                {addingSpecialista ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : editingSpecialista ? (
                  <Save className="w-4 h-4 mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                {editingSpecialista ? 'Salva' : 'Aggiungi'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Template Placeholder Info - Compatto */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading">Placeholder per Template</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5 text-xs">
              {[
                '{{CODICE}}', '{{DATA}}', '{{RAGIONE_SOCIALE}}', '{{EMAIL}}',
                '{{INDIRIZZO}}', '{{CITTA}}', '{{CAP}}', '{{PARTITA_IVA}}',
                '{{CODICE_FISCALE}}', '{{TELEFONO}}', '{{PEC}}', '{{RIFERIMENTO}}',
                '{{SDI}}', '{{SERVIZI}}', '{{TABELLA_SERVIZI}}', '{{TOTALE_LISTINO}}',
                '{{SCONTO}}', '{{TOTALE_SCONTATO}}', '{{MODALITA_PAGAMENTO}}',
                '{{VALIDITA}}', '{{NOTE}}', '{{TEMPI_REALIZZAZIONE}}',
                '{{SPECIALISTI}}', '{{MATERIALE_CLIENTE}}'
              ].map((placeholder) => (
                <code
                  key={placeholder}
                  className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 font-mono"
                >
                  {placeholder}
                </code>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* System Upgrade */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Download className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg font-heading">Aggiornamento Sistema</CardTitle>
                <CardDescription>Aggiorna l'applicazione da GitHub</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Version Info */}
            {versionInfo && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <GitBranch className="w-5 h-5 text-slate-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">
                    Branch: <span className="font-mono">{versionInfo.branch}</span>
                  </p>
                  <p className="text-xs text-slate-500 font-mono truncate">
                    {versionInfo.last_commit}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchVersionInfo}
                  className="h-8 w-8 p-0"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Upgrade Result */}
            {upgradeResult && (
              <div className={`p-3 rounded-lg border ${upgradeResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <p className={`text-sm font-medium ${upgradeResult.success ? 'text-emerald-700' : 'text-red-700'}`}>
                  {upgradeResult.message}
                </p>
                {upgradeResult.steps_completed && upgradeResult.steps_completed.length > 0 && (
                  <ul className="mt-2 text-xs text-slate-600 space-y-1">
                    {upgradeResult.steps_completed.map((step, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Check className="w-3 h-3 text-emerald-500" />
                        {step}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => setUpgradeDialogOpen(true)}
                disabled={upgrading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="upgrade-btn"
              >
                {upgrading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Aggiornamento...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    UPGRADE
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-slate-500">
              Esegue git pull dal repository e ricompila il frontend. Dopo l'upgrade, riavvia il servizio backend per applicare le modifiche al server.
            </p>
          </CardContent>
        </Card>

        {/* Backup System */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <HardDrive className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg font-heading">Backup Sistema</CardTitle>
                <CardDescription>Esegui il backup di software e database su FTP</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Backup Result */}
            {backupResult && (
              <div className={`p-3 rounded-lg border ${backupResult.success ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
                <p className={`text-sm font-medium ${backupResult.success ? 'text-blue-700' : 'text-red-700'}`}>
                  {backupResult.message}
                </p>
                {backupResult.backup_file && (
                  <p className="mt-1 text-xs text-slate-600 font-mono">
                    File: {backupResult.backup_file}
                  </p>
                )}
                {backupResult.steps_completed && backupResult.steps_completed.length > 0 && (
                  <ul className="mt-2 text-xs text-slate-600 space-y-1">
                    {backupResult.steps_completed.map((step, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Check className="w-3 h-3 text-blue-500" />
                        {step}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => setBackupDialogOpen(true)}
                disabled={backingUp}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="backup-btn"
              >
                {backingUp ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Backup in corso...
                  </>
                ) : (
                  <>
                    <HardDrive className="w-4 h-4 mr-2" />
                    BACKUP
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-slate-500">
              Crea un archivio compresso con il database MongoDB e i file del progetto (backend e frontend) e lo carica sul server FTP configurato.
            </p>
          </CardContent>
        </Card>

        {/* Upgrade Confirmation Dialog */}
        <AlertDialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Conferma Upgrade</AlertDialogTitle>
              <AlertDialogDescription>
                Questa operazione eseguirà:
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Git pull dal repository</li>
                  <li>Aggiornamento dipendenze backend</li>
                  <li>Ricompilazione frontend</li>
                </ul>
                <p className="mt-3 font-medium text-amber-600">
                  Dopo l'upgrade, ricordati di riavviare il servizio backend sul server.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setUpgradeDialogOpen(false);
                  handleUpgrade();
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Procedi con l'Upgrade
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Backup Confirmation Dialog */}
        <AlertDialog open={backupDialogOpen} onOpenChange={setBackupDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Conferma Backup</AlertDialogTitle>
              <AlertDialogDescription>
                Questa operazione eseguirà:
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Export del database MongoDB</li>
                  <li>Copia dei file backend e frontend</li>
                  <li>Creazione archivio compresso</li>
                  <li>Upload su server FTP</li>
                </ul>
                <p className="mt-3 text-slate-600">
                  L'operazione potrebbe richiedere alcuni minuti.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setBackupDialogOpen(false);
                  handleBackup();
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Esegui Backup
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Password Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Cambia Password</DialogTitle>
              <DialogDescription>
                Inserisci la tua password attuale e quella nuova
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Password Attuale</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nuova Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Conferma Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                  Annulla
                </Button>
                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {changingPassword ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Aggiorna
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Template Selection Dialog - Using DriveBrowser */}
        <DriveBrowser
          open={templateDialogOpen}
          onOpenChange={setTemplateDialogOpen}
          onSelect={handleSelectTemplate}
          mode="template"
          title="Seleziona Modello"
          description="Naviga nel tuo Drive e seleziona un Google Doc come modello"
        />

        {/* Folder Selection Dialog - Using DriveBrowser */}
        <DriveBrowser
          open={folderDialogOpen}
          onOpenChange={setFolderDialogOpen}
          onSelect={handleSelectFolder}
          mode="folder"
          title="Seleziona Cartella"
          description="Naviga nel tuo Drive e seleziona dove salvare i preventivi"
        />

        {/* Disconnect Dialog */}
        <AlertDialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Disconnetti Google Drive</AlertDialogTitle>
              <AlertDialogDescription>
                Sei sicuro di voler disconnettere Google Drive? Non potrai più generare documenti fino a quando non riconnetti l'account.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDisconnectDrive}
                disabled={disconnecting}
                className="bg-red-600 hover:bg-red-700"
              >
                {disconnecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Disconnetti
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default Impostazioni;
